import { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, FormControl, FormLabel, Input,
    Modal, ModalDialog, Option, Select, Sheet, Stack, Switch, Tab, TabList, Tabs,
    Table, Textarea, Typography,
} from '@mui/joy';

import { useAdmin } from '../../AdminContext';
import {
    APIAdminCities, APIAdminCityCreate, APIAdminCityUpdate, APIAdminCityDelete,
    APIAdminPlaceCategories, APIAdminPlaceCategoryCreate, APIAdminPlaceCategoryUpdate, APIAdminPlaceCategoryDelete,
    APIAdminPlaces, APIAdminPlaceCreate, APIAdminPlaceUpdate, APIAdminPlaceDelete,
    placesError,
} from '../../api_admin_places';

// ============================================================ ГОРОДА ===
function CitiesTab() {
    const { adminToken: token } = useAdmin();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modal, setModal] = useState(null); // { city|null }
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try { const r = await APIAdminCities(token); setItems(r?.items || []); }
        catch (e) { setError(placesError(e)); } finally { setLoading(false); }
    }, [token]);
    useEffect(() => { load(); }, [load]);

    const openCreate = () => { setForm({ name: '', region: '', latitude: '', longitude: '', is_active: true }); setModal({ city: null }); };
    const openEdit = (c) => { setForm({ name: c.name, region: c.region, latitude: c.latitude ?? '', longitude: c.longitude ?? '', is_active: c.is_active }); setModal({ city: c }); };

    const save = async () => {
        setSaving(true); setError(null);
        try {
            if (modal.city) await APIAdminCityUpdate(token, modal.city.id, form);
            else await APIAdminCityCreate(token, form);
            setModal(null); await load();
        } catch (e) { setError(placesError(e)); } finally { setSaving(false); }
    };
    const remove = async (c) => {
        if (!window.confirm(`Удалить город «${c.name}»?`)) return;
        try { await APIAdminCityDelete(token, c.id); await load(); }
        catch (e) { setError(placesError(e)); }
    };

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography level="title-md">Города</Typography>
                <Button size="sm" onClick={openCreate}>Добавить город</Button>
            </Stack>
            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}
            {loading ? <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack> : (
                <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                    <Table hoverRow sx={{ '--TableCell-paddingY': '10px' }}>
                        <thead><tr><th>Город</th><th style={{ width: 180 }}>Регион</th><th style={{ width: 90 }}>Точек</th><th style={{ width: 110 }}>Статус</th><th style={{ width: 150 }}>Действия</th></tr></thead>
                        <tbody>
                            {items.map((c) => (
                                <tr key={c.id}>
                                    <td><Typography level="body-sm" sx={{ fontWeight: 600 }}>{c.name}</Typography></td>
                                    <td>{c.region || '—'}</td>
                                    <td>{c.places_count}</td>
                                    <td><Chip size="sm" color={c.is_active ? 'success' : 'neutral'} variant="soft">{c.is_active ? 'активен' : 'выкл'}</Chip></td>
                                    <td>
                                        <Stack direction="row" spacing={0.5}>
                                            <Button size="sm" variant="soft" onClick={() => openEdit(c)}>Изм.</Button>
                                            <Button size="sm" variant="soft" color="danger" onClick={() => remove(c)}>Удал.</Button>
                                        </Stack>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && <tr><td colSpan={5}><Typography level="body-sm" color="neutral" sx={{ py: 2, textAlign: 'center' }}>Городов нет</Typography></td></tr>}
                        </tbody>
                    </Table>
                </Sheet>
            )}

            {modal && (
                <Modal open onClose={() => setModal(null)}>
                    <ModalDialog sx={{ maxWidth: 440, width: '100%' }}>
                        <Typography level="h4">{modal.city ? 'Город' : 'Новый город'}</Typography>
                        <Stack spacing={1.5} sx={{ mt: 1 }}>
                            <FormControl><FormLabel>Название</FormLabel>
                                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormControl>
                            <FormControl><FormLabel>Регион</FormLabel>
                                <Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} /></FormControl>
                            <Stack direction="row" spacing={1.5}>
                                <FormControl sx={{ flex: 1 }}><FormLabel>Широта</FormLabel>
                                    <Input type="number" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} /></FormControl>
                                <FormControl sx={{ flex: 1 }}><FormLabel>Долгота</FormLabel>
                                    <Input type="number" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} /></FormControl>
                            </Stack>
                            <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
                                <FormLabel>Активен</FormLabel>
                                <Switch checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                            </FormControl>
                            <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
                                <Button loading={saving} onClick={save}>Сохранить</Button>
                                <Button variant="outlined" color="neutral" onClick={() => setModal(null)}>Отмена</Button>
                            </Stack>
                        </Stack>
                    </ModalDialog>
                </Modal>
            )}
        </Box>
    );
}

// ========================================================= КАТЕГОРИИ ===
function CategoriesTab() {
    const { adminToken: token } = useAdmin();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try { const r = await APIAdminPlaceCategories(token); setItems(r?.items || []); }
        catch (e) { setError(placesError(e)); } finally { setLoading(false); }
    }, [token]);
    useEffect(() => { load(); }, [load]);

    const openCreate = () => { setForm({ name: '', icon: '', order: 0, is_active: true }); setModal({ cat: null }); };
    const openEdit = (c) => { setForm({ name: c.name, icon: c.icon, order: c.order, is_active: c.is_active }); setModal({ cat: c }); };

    const save = async () => {
        setSaving(true); setError(null);
        try {
            if (modal.cat) await APIAdminPlaceCategoryUpdate(token, modal.cat.id, form);
            else await APIAdminPlaceCategoryCreate(token, form);
            setModal(null); await load();
        } catch (e) { setError(placesError(e)); } finally { setSaving(false); }
    };
    const remove = async (c) => {
        if (!window.confirm(`Удалить категорию «${c.name}»?`)) return;
        try { await APIAdminPlaceCategoryDelete(token, c.id); await load(); }
        catch (e) { setError(placesError(e)); }
    };

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography level="title-md">Категории</Typography>
                <Button size="sm" onClick={openCreate}>Добавить категорию</Button>
            </Stack>
            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}
            {loading ? <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack> : (
                <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                    <Table hoverRow sx={{ '--TableCell-paddingY': '10px' }}>
                        <thead><tr><th>Категория</th><th style={{ width: 120 }}>Код</th><th style={{ width: 90 }}>Порядок</th><th style={{ width: 90 }}>Точек</th><th style={{ width: 110 }}>Статус</th><th style={{ width: 150 }}>Действия</th></tr></thead>
                        <tbody>
                            {items.map((c) => (
                                <tr key={c.id}>
                                    <td><Typography level="body-sm" sx={{ fontWeight: 600 }}>{c.name}</Typography></td>
                                    <td><Typography level="body-xs" color="neutral">{c.slug}</Typography></td>
                                    <td>{c.order}</td>
                                    <td>{c.places_count}</td>
                                    <td><Chip size="sm" color={c.is_active ? 'success' : 'neutral'} variant="soft">{c.is_active ? 'активна' : 'выкл'}</Chip></td>
                                    <td>
                                        <Stack direction="row" spacing={0.5}>
                                            <Button size="sm" variant="soft" onClick={() => openEdit(c)}>Изм.</Button>
                                            <Button size="sm" variant="soft" color="danger" onClick={() => remove(c)}>Удал.</Button>
                                        </Stack>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && <tr><td colSpan={6}><Typography level="body-sm" color="neutral" sx={{ py: 2, textAlign: 'center' }}>Категорий нет</Typography></td></tr>}
                        </tbody>
                    </Table>
                </Sheet>
            )}

            {modal && (
                <Modal open onClose={() => setModal(null)}>
                    <ModalDialog sx={{ maxWidth: 420, width: '100%' }}>
                        <Typography level="h4">{modal.cat ? 'Категория' : 'Новая категория'}</Typography>
                        <Stack spacing={1.5} sx={{ mt: 1 }}>
                            <FormControl><FormLabel>Название</FormLabel>
                                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormControl>
                            <Stack direction="row" spacing={1.5}>
                                <FormControl sx={{ flex: 1 }}><FormLabel>Иконка (эмодзи/код)</FormLabel>
                                    <Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} /></FormControl>
                                <FormControl sx={{ width: 120 }}><FormLabel>Порядок</FormLabel>
                                    <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></FormControl>
                            </Stack>
                            <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
                                <FormLabel>Активна</FormLabel>
                                <Switch checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                            </FormControl>
                            <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
                                <Button loading={saving} onClick={save}>Сохранить</Button>
                                <Button variant="outlined" color="neutral" onClick={() => setModal(null)}>Отмена</Button>
                            </Stack>
                        </Stack>
                    </ModalDialog>
                </Modal>
            )}
        </Box>
    );
}

// ============================================================ ТОЧКИ ===
function PlacesTab() {
    const { adminToken: token } = useAdmin();
    const [items, setItems] = useState([]);
    const [cities, setCities] = useState([]);
    const [categories, setCategories] = useState([]);
    const [filters, setFilters] = useState({ city_id: '', category_id: '', search: '' });
    const [searchInput, setSearchInput] = useState('');
    const [pages, setPages] = useState(1);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modal, setModal] = useState(null); // { place|null }
    const [form, setForm] = useState({});
    const [imageFile, setImageFile] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        APIAdminCities(token).then((r) => setCities(r?.items || [])).catch(() => {});
        APIAdminPlaceCategories(token).then((r) => setCategories(r?.items || [])).catch(() => {});
    }, [token]);

    useEffect(() => {
        const t = setTimeout(() => { setPage(1); setFilters((f) => ({ ...f, search: searchInput })); }, 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const r = await APIAdminPlaces(token, { ...filters, page, page_size: 30 });
            setItems(r?.items || []); setPages(r?.pages || 1);
        } catch (e) { setError(placesError(e)); } finally { setLoading(false); }
    }, [token, filters, page]);
    useEffect(() => { load(); }, [load]);

    const openCreate = () => {
        setForm({ name: '', city_id: '', category_id: '', address: '', description: '', contact_phone: '', website: '', is_active: true, image_url: null });
        setImageFile(null); setModal({ place: null });
    };
    const openEdit = (p) => {
        setForm({
            name: p.name, city_id: String(p.city.id), category_id: String(p.category.id),
            address: p.address, description: p.description, contact_phone: p.contact_phone,
            website: p.website, is_active: p.is_active, image_url: p.image_url,
        });
        setImageFile(null); setModal({ place: p });
    };

    const save = async () => {
        if (!form.name.trim() || !form.city_id || !form.category_id) { setError('Название, город и категория обязательны'); return; }
        setSaving(true); setError(null);
        try {
            const fd = new FormData();
            fd.append('name', form.name.trim());
            fd.append('city_id', form.city_id);
            fd.append('category_id', form.category_id);
            fd.append('address', form.address || '');
            fd.append('description', form.description || '');
            fd.append('contact_phone', form.contact_phone || '');
            fd.append('website', form.website || '');
            fd.append('is_active', form.is_active ? 'true' : 'false');
            if (imageFile) fd.append('image', imageFile);
            if (modal.place) await APIAdminPlaceUpdate(token, modal.place.id, fd);
            else await APIAdminPlaceCreate(token, fd);
            setModal(null); await load();
        } catch (e) { setError(placesError(e)); } finally { setSaving(false); }
    };
    const remove = async (p) => {
        if (!window.confirm(`Удалить точку «${p.name}»?`)) return;
        try { await APIAdminPlaceDelete(token, p.id); await load(); }
        catch (e) { setError(placesError(e)); }
    };

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography level="title-md">Точки</Typography>
                <Button size="sm" onClick={openCreate}>Добавить точку</Button>
            </Stack>

            <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'md', mb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Input size="sm" placeholder="Поиск" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} sx={{ flex: 1 }} />
                    <Select size="sm" placeholder="Город" value={filters.city_id} sx={{ minWidth: 160 }}
                            onChange={(_, v) => { setPage(1); setFilters({ ...filters, city_id: v ?? '' }); }}>
                        <Option value="">Все города</Option>
                        {cities.map((c) => <Option key={c.id} value={String(c.id)}>{c.name}</Option>)}
                    </Select>
                    <Select size="sm" placeholder="Категория" value={filters.category_id} sx={{ minWidth: 160 }}
                            onChange={(_, v) => { setPage(1); setFilters({ ...filters, category_id: v ?? '' }); }}>
                        <Option value="">Все категории</Option>
                        {categories.map((c) => <Option key={c.id} value={String(c.id)}>{c.name}</Option>)}
                    </Select>
                </Stack>
            </Sheet>

            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}
            {loading ? <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack> : (
                <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                    <Table hoverRow sx={{ '--TableCell-paddingY': '10px' }}>
                        <thead><tr><th style={{ width: 56 }}></th><th>Название</th><th style={{ width: 140 }}>Город</th><th style={{ width: 130 }}>Категория</th><th style={{ width: 100 }}>Статус</th><th style={{ width: 150 }}>Действия</th></tr></thead>
                        <tbody>
                            {items.map((p) => (
                                <tr key={p.id}>
                                    <td>{p.image_url && <img src={p.image_url} alt="" width={40} height={40} style={{ objectFit: 'cover', borderRadius: 6 }} />}</td>
                                    <td><Typography level="body-sm" sx={{ fontWeight: 600 }}>{p.name}</Typography>
                                        {p.address && <Typography level="body-xs" color="neutral">{p.address}</Typography>}</td>
                                    <td>{p.city?.name}</td>
                                    <td><Chip size="sm" variant="soft">{p.category?.name}</Chip></td>
                                    <td><Chip size="sm" color={p.is_active ? 'success' : 'neutral'} variant="soft">{p.is_active ? 'активна' : 'выкл'}</Chip></td>
                                    <td>
                                        <Stack direction="row" spacing={0.5}>
                                            <Button size="sm" variant="soft" onClick={() => openEdit(p)}>Изм.</Button>
                                            <Button size="sm" variant="soft" color="danger" onClick={() => remove(p)}>Удал.</Button>
                                        </Stack>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && <tr><td colSpan={6}><Typography level="body-sm" color="neutral" sx={{ py: 2, textAlign: 'center' }}>Точек нет</Typography></td></tr>}
                        </tbody>
                    </Table>
                </Sheet>
            )}

            {pages > 1 && (
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                    <Button size="sm" variant="outlined" color="neutral" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Назад</Button>
                    <Typography level="body-sm" sx={{ alignSelf: 'center' }}>{page} / {pages}</Typography>
                    <Button size="sm" variant="outlined" color="neutral" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Вперёд</Button>
                </Stack>
            )}

            {modal && (
                <Modal open onClose={() => setModal(null)}>
                    <ModalDialog sx={{ maxWidth: 560, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
                        <Typography level="h4">{modal.place ? 'Точка' : 'Новая точка'}</Typography>
                        <Stack spacing={1.5} sx={{ mt: 1 }}>
                            <FormControl><FormLabel>Название</FormLabel>
                                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormControl>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                <FormControl sx={{ flex: 1 }}><FormLabel>Город</FormLabel>
                                    <Select value={form.city_id} onChange={(_, v) => setForm({ ...form, city_id: v })}>
                                        {cities.map((c) => <Option key={c.id} value={String(c.id)}>{c.name}</Option>)}
                                    </Select></FormControl>
                                <FormControl sx={{ flex: 1 }}><FormLabel>Категория</FormLabel>
                                    <Select value={form.category_id} onChange={(_, v) => setForm({ ...form, category_id: v })}>
                                        {categories.map((c) => <Option key={c.id} value={String(c.id)}>{c.name}</Option>)}
                                    </Select></FormControl>
                            </Stack>
                            <FormControl><FormLabel>Адрес</FormLabel>
                                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></FormControl>
                            <FormControl><FormLabel>Описание</FormLabel>
                                <Textarea minRows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormControl>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                <FormControl sx={{ flex: 1 }}><FormLabel>Телефон</FormLabel>
                                    <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></FormControl>
                                <FormControl sx={{ flex: 1 }}><FormLabel>Сайт</FormLabel>
                                    <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></FormControl>
                            </Stack>
                            <FormControl><FormLabel>Фото</FormLabel>
                                {form.image_url && !imageFile && <img src={form.image_url} alt="" width={80} height={80} style={{ objectFit: 'cover', borderRadius: 8, marginBottom: 8 }} />}
                                <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files?.[0] || null)} />
                            </FormControl>
                            <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
                                <FormLabel>Активна</FormLabel>
                                <Switch checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                            </FormControl>
                            <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
                                <Button loading={saving} onClick={save}>Сохранить</Button>
                                <Button variant="outlined" color="neutral" onClick={() => setModal(null)}>Отмена</Button>
                            </Stack>
                        </Stack>
                    </ModalDialog>
                </Modal>
            )}
        </Box>
    );
}

export default function AdminPlacesPage() {
    const [tab, setTab] = useState(0);
    return (
        <Box>
            <Typography level="h4" sx={{ mb: 2 }}>Места</Typography>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ bgcolor: 'transparent', mb: 2 }}>
                <TabList>
                    <Tab>Точки</Tab>
                    <Tab>Города</Tab>
                    <Tab>Категории</Tab>
                </TabList>
            </Tabs>
            {tab === 0 && <PlacesTab />}
            {tab === 1 && <CitiesTab />}
            {tab === 2 && <CategoriesTab />}
        </Box>
    );
}